import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowRight, Building2, Check, ClipboardList, MapPin,
  Plus, RefreshCw, Store, Trash2,
} from 'lucide-react';
import SearchableDropdown from '../SearchableDropdown';
import { toastService } from '../../services/toastService';
import {
  billingApi, buildPlanDraftBody, describeDiscount, discountInputError,
  BLANK_DISCOUNT, discountToInput,
  adminNotifiedSentence, customerNotifiedSentence,
  indeterminatePublishReport, isIndeterminateActivation, isPlanActivationRefusal,
  locationChangeRows, materialisePlacement, overallDiscountForTarget, overallDiscountUnverified,
  packageKeyOf, payableMonthlyCents, preOverallCents, publishNoOp, publishReport, togglePlacement,
  type ComposeError, type DiscountInput, type LocationPackageChange, type PlanAssignmentRow,
  type PlanComposition, type PlanChangeSummary, type PlanDiscount, type PublishReport,
} from '../../services/billingApi';
import { Card, CardHeader, Chip, ConfirmDialog, Note } from './financeUi';
import DiscountFields from './DiscountFields';
import { money, shortDate, type LocationRow } from './financeFormat';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
/**
 * FINANCE → PLAN → PLAN ASSIGNMENT BUILDER
 *
 * The screen where a human decides what a customer pays: pick the packages, see
 * their list prices, discount them by percent or by amount until the number is
 * the number that was agreed — and see, package by package and location by
 * location, exactly how that number was arrived at.
 *
 * ONE RULE RUNS THROUGH ALL OF IT: **every amount on this screen comes back from
 * `POST /plan-assignments/{tenantId}/preview`.** There is no arithmetic here.
 * `composeSubscription` is one pure function on the server precisely so the
 * quote an operator approves and the amount Stripe charges cannot drift apart,
 * and a second copy of it in the browser would defeat that within a month.
 * Everything this file computes is a SUM OF SERVER NUMBERS or a comparison —
 * never a discount, never a net.
 *
 * The consequences of that rule, made explicit because each one is a bug that
 * would otherwise be invisible:
 *
 *  - `payableMonthlyCents()` is a LOOKUP of `totals.netCents`. The overall
 *    discount is already apportioned into the line nets, so subtracting
 *    `subscriptionDiscount` would show a customer a double-discounted price.
 *  - The totals are marked STALE the moment the plan differs from the plan they
 *    were computed from, and **Activate is disabled while they are stale** —
 *    otherwise the number on screen is a number for a plan that no longer exists.
 *  - **Activate is disabled while the preview reports any error**, and each
 *    error names its package, on the package's own row as well as in the list.
 *  - The CURRENT bill is read by composing the ACTIVE assignment server-side,
 *    not by adding up what the browser happens to know.
 *
 * ---------------------------------------------------------------------------
 * TWO CALLERS, ONE COMPONENT (spec §14/C2)
 * ---------------------------------------------------------------------------
 *
 * The Plan panel opens this to build a tenant-wide plan. The Locations panel's
 * **Change price** opens the SAME component with `focusLocationId` set, scoped
 * to one store. A second, divergent copy of this screen is the worst available
 * outcome — two editors of the same money that agree until the day they do not
 * — so the location editor is a MODE here, never a fork.
 *
 * What the mode changes, and nothing else:
 *  - each package row shows what THIS store is charged for it, and where else
 *    it bills;
 *  - each package row gains an "billed here" switch, which edits the
 *    assignment's `locationPackageIds` placement;
 *  - a package added from this editor is placed at THIS store, not everywhere.
 *
 * What it deliberately does NOT change: discounts. A per-package discount and
 * the overall discount are properties of the ASSIGNMENT, so they apply to every
 * store the package bills at. That is said on the screen rather than hidden
 * behind a per-location-looking control that would quietly reprice other stores.
 *
 * ---------------------------------------------------------------------------
 * THE BEFORE -> AFTER GATE
 * ---------------------------------------------------------------------------
 *
 * Nobody's bill changes without a human seeing both numbers — so the
 * confirmation shows the change PACKAGE BY PACKAGE, and the operator must
 * explicitly acknowledge it before Publish is enabled. Both halves of that
 * comparison come back from `POST …/preview` with `compareToActive: true`: the
 * "before" is the server composing the assignment actually in force, never this
 * screen reassembling one from what it loaded.
 */

interface Props {
  tenantId: string;
  /** Catalog SKUs, already loaded by the Finance tab. */
  skus: any[];
  /** This tenant's locations — for naming the ids the composition comes back with. */
  locations: LocationRow[];
  /**
   * The base plan plus the per-location subscriptions — the lines this
   * composition DOES NOT TOUCH.
   *
   * Under spec §13/B1 the composed net becomes ONE managed software line per
   * location that is ADDED BESIDE the location's existing base plan line;
   * `subscriptionSync` never names a line it did not stamp. So the composed
   * figure is not this tenant's whole bill and must never be presented as
   * replacing it — it is shown alongside, stated as unchanged, so the
   * before/after comparison is composed-against-composed and the rest of the
   * bill is visible rather than implied away.
   */
  baseSubscriptionCents: number;
  /** Reload the tenant record after an activation. */
  onActivated: () => Promise<void> | void;
  /**
   * Scope the editor to ONE location — the Locations panel's "Change price".
   *
   * It is the location's `locationId` (never its `requestId`): the composition
   * comes back keyed by `locationId`, and a location still awaiting approval has
   * no `locationId` at all and therefore nothing to scope to.
   */
  focusLocationId?: string;
  focusLocationName?: string;
  /** Rendered in the header of the location editor, next to the store's name. */
  headerHint?: string;
}

// ---------------------------------------------------------------------------
// Catalog projection
// ---------------------------------------------------------------------------

interface PackageOption {
  /** The key an assignment names this SKU by — see `packageKeyOf`. */
  key: string;
  name: string;
  amountCents: number;
  scope: 'tenant' | 'location';
  type: 'plan' | 'package';
  description?: string;
  deprecated: boolean;
  /** Every catalog row claiming this key. More than one is a catalog fault. */
  skuIds: string[];
}

/**
 * `BLANK_DISCOUNT` and `discountToInput` now live in `billingApi.ts` and are
 * SHARED with the Deals builder, which edits the same `DiscountSchema` shape.
 * They used to be private to this file; two copies of "a stored discount, back
 * into a form" is precisely how two screens start disagreeing about what one
 * stored discount is worth.
 */

// Exported so a plain (no-DOM) test can assert every `ComposeWarningCode` has
// an entry here, rather than silently falling back to the generic "Worth
// knowing" — see billingApi.test.ts.
export const WARNING_TITLE: Record<string, string> = {
  empty_plan: 'This plan contains no packages',
  no_locations: 'This tenant has no active locations',
  free_package: 'Priced at zero in the catalog',
  fully_discounted: 'Discounted to zero',
  placed_nowhere: 'Sold, but billing at no location',
  trial_at_creation_only: 'This plan grants a trial',
  location_bills_nothing: 'A store on this tenant is billed nothing',
};

const CHANGE_LABEL: Record<string, string> = {
  added: 'Added',
  removed: 'Removed',
  repriced: 'Price changed',
  unchanged: 'No change',
};

const CHANGE_TONE: Record<string, string> = {
  added: 'emerald',
  removed: 'red',
  repriced: 'amber',
  unchanged: 'stone',
};

/**
 * Errors on the plan actually IN FORCE — extracted to its own component so it
 * is testable via `renderToStaticMarkup` without a DOM. `PlanAssignmentBuilder`
 * itself cannot be rendered that way: its `current` state is set from
 * billing-service inside a `useEffect`, which never runs during a
 * render-only/SSR pass, so there is no way to drive it from outside without a
 * DOM-backed renderer this repo does not have (and must not gain).
 *
 * `planAssignment.ts`'s preview returns compose errors inside a 200, so a
 * tenant whose active, sold plan contains a package with no SKU would
 * otherwise show a clean "Packages billing today" figure with no sign that a
 * package they were sold cannot be priced. Same visual language as the new
 * plan's errors (the `composeErrors` block below).
 */
export const ActivePlanErrorsNote: React.FC<{ errors: { packageId: string; reason: string; name: string }[] }> = (
  { errors },
) => {
  if (errors.length === 0) return null;
  return (
    <Note tone="danger">
      <strong>
        {errors.length} package(s) on the ACTIVE plan cannot be priced — not included in
        &quot;Packages billing today&quot; above.
      </strong>
      <ul className="mt-1.5 space-y-0.5">
        {errors.map((e, i) => (
          <li key={`current-${e.packageId}-${i}`}>
            <strong>{e.name}</strong>
            <span className="font-mono text-[11px] text-red-700"> ({e.packageId})</span>: {e.reason}
          </li>
        ))}
      </ul>
    </Note>
  );
};

const PlanAssignmentBuilder: React.FC<Props> = ({
  tenantId, skus, locations, baseSubscriptionCents, onActivated,
  focusLocationId, focusLocationName, headerHint,
}) => {
  // -- what the tenant is on today ----------------------------------------
  const [active, setActive] = useState<PlanAssignmentRow | null>(null);
  const [extraActive, setExtraActive] = useState<PlanAssignmentRow[]>([]);
  const [current, setCurrent] = useState<PlanComposition | null>(null);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [resumable, setResumable] = useState<PlanAssignmentRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // -- the plan being built -------------------------------------------------
  const [selected, setSelected] = useState<string[]>([]);
  const [perPackage, setPerPackage] = useState<Record<string, DiscountInput>>({});
  const [overall, setOverall] = useState<DiscountInput>({ ...BLANK_DISCOUNT });
  const [placement, setPlacement] = useState<Record<string, string[]> | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  /**
   * Whether the draft `draftId` points at carries a `locationPackageIds`.
   *
   * A draft REPLACE merges the body over the stored row, and there is no value
   * that means "delete this key" — `{}` means "placed nowhere" (bills nothing)
   * and `null` fails the schema. So a stored placement CANNOT be removed by
   * replacing. Tracked here so `saveDraft` can mint a fresh draft in that one
   * case instead, rather than storing a plan that composes to a different
   * number than the one on screen.
   */
  const [draftHasPlacement, setDraftHasPlacement] = useState(false);

  // -- the server's arithmetic for that plan --------------------------------
  const [preview, setPreview] = useState<PlanComposition | null>(null);
  const [previewedKey, setPreviewedKey] = useState<string>('');
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [refusal, setRefusal] = useState<ComposeError[] | null>(null);
  /**
   * The before → after gate. Publish stays disabled until the operator has
   * ticked this, and it is cleared every time the plan changes — so an
   * acknowledgement can only ever apply to the comparison that was on screen
   * when it was given.
   */
  const [acknowledged, setAcknowledged] = useState(false);
  /**
   * What the last publish actually did to the money, from billing-service.
   *
   * PERSISTENT, not a toast. "The plan was recorded but Stripe was not updated"
   * is the one outcome an operator must not miss, and a toast that has faded is
   * exactly how they miss it.
   */
  const [lastPublish, setLastPublish] = useState<PublishReport | null>(null);
  /** "Make it $X a month" — see `applyTargetPrice`. Dollars, as typed. */
  const [targetPrice, setTargetPrice] = useState('');
  const [targetError, setTargetError] = useState<string | null>(null);

  // -- catalog --------------------------------------------------------------
  const catalog = useMemo(() => {
    const byKey = new Map<string, PackageOption>();
    for (const s of skus || []) {
      if (s?.type !== 'package' && s?.type !== 'plan') continue;
      const key = packageKeyOf(s);
      if (!key) continue;
      const existing = byKey.get(key);
      if (existing) { existing.skuIds.push(String(s.skuId)); continue; }
      byKey.set(key, {
        key,
        name: String(s.name || key),
        amountCents: Number(s.amountCents || 0),
        scope: s.scope === 'tenant' ? 'tenant' : 'location',
        type: s.type,
        description: s.description,
        deprecated: s.status === 'deprecated',
        skuIds: [String(s.skuId)],
      });
    }
    return byKey;
  }, [skus]);

  const addOptions = useMemo(
    () => Array.from(catalog.values())
      .filter((o) => !o.deprecated && !selected.includes(o.key))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((o) => ({
        value: o.key,
        label: o.name,
        subtitle: `${money(o.amountCents)}/mo · ${o.scope === 'tenant' ? 'billed once' : 'per store'}`,
      })),
    [catalog, selected],
  );

  /** Location names keyed by BOTH ids — composition returns `locationId`, the request row carries `requestId`. */
  const locationNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const l of locations || []) {
      const name = l.name || l.locationId || l.requestId;
      if (l.locationId) map[l.locationId] = name;
      if (l.requestId) map[l.requestId] = name;
    }
    return map;
  }, [locations]);

  // -- seeding --------------------------------------------------------------
  const seedFrom = useCallback((row: PlanAssignmentRow | null) => {
    setSelected(row ? [...row.planPackageIds] : []);
    setPerPackage(
      row
        ? Object.fromEntries(row.planPackageIds.map((k) => [k, discountToInput(row.discounts?.perPackage?.[k])]))
        : {},
    );
    setOverall(discountToInput(row?.discounts?.overall));
    // Carried forward, never invented. An assignment written by onboarding
    // places packages explicitly (a free-location quota, or packages chosen for
    // one store and not another) and dropping that placement here would silently
    // reprice the tenant the moment this plan is activated.
    setPlacement(row?.locationPackageIds ? { ...row.locationPackageIds } : null);
    setDraftId(row?.status === 'draft' ? row.assignmentId : null);
    setDraftHasPlacement(row?.status === 'draft' && !!row.locationPackageIds);
    setRefusal(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setCurrentError(null);
    try {
      // `?status=active` is the ONLY correct read of the current plan — see the
      // client method. A client-side filter over the first page silently reads a
      // tenant with a page of drafts as having no plan at all.
      const [activeRes, historyRes] = await Promise.allSettled([
        billingApi.getActivePlanAssignment(tenantId),
        billingApi.listPlanAssignments(tenantId, { limit: 25 }),
      ]);

      if (activeRes.status === 'rejected') {
        // "Could not read" and "there is none" are completely different facts
        // about a tenant's money. Never let the first render as the second.
        setLoadError(activeRes.reason?.message || 'The active plan assignment could not be read.');
        setActive(null);
        setCurrent(null);
        setLoading(false);
        return;
      }

      const rows = activeRes.value.items || [];
      const activeRow = rows[0] || null;
      setActive(activeRow);
      setExtraActive(rows.slice(1));
      setResumable(
        historyRes.status === 'fulfilled'
          ? (historyRes.value.items || []).find((r) => r.status === 'draft') || null
          : null,
      );
      seedFrom(activeRow);

      if (activeRow) {
        try {
          setCurrent(await billingApi.previewPlanAssignment(tenantId, { assignmentId: activeRow.assignmentId }));
        } catch (e: any) {
          setCurrent(null);
          setCurrentError(e?.message || 'The current plan could not be priced.');
        }
      } else {
        setCurrent(null);
      }
    } catch (e: any) {
      // A 200 with a null/empty body — a 204 becomes `undefined` at `req()` —
      // throws reading `.items` above, which without this catch falls through
      // to "No plan assignment yet": the exact confusion this screen exists to
      // guard against elsewhere, on top of leaving an unhandled rejection.
      // Render the SAME "could not be read" state the rejected-promise branch
      // above already renders, rather than inventing a second one.
      setLoadError(e?.message || 'The active plan assignment could not be read.');
      setActive(null);
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId, seedFrom]);

  useEffect(() => { load(); }, [load]);

  // -- the request body, and the plan it describes --------------------------
  //
  // ONE builder for preview, save and activate, so the plan that was priced is
  // byte-for-byte the plan that is stored and charged. `assignmentId` is added
  // only at save time, so minting a draft does not invalidate the preview.
  const body = useMemo(
    () => buildPlanDraftBody({ planPackageIds: selected, perPackage, overall, locationPackageIds: placement }),
    [selected, perPackage, overall, placement],
  );
  const bodyKey = useMemo(() => JSON.stringify(body), [body]);

  const inputErrors = useMemo(() => {
    const list: { key: string; label: string; message: string }[] = [];
    for (const key of selected) {
      const message = discountInputError(perPackage[key]);
      if (message) list.push({ key, label: catalog.get(key)?.name || key, message });
    }
    const overallMessage = discountInputError(overall);
    if (overallMessage) list.push({ key: '__overall', label: 'Overall discount', message: overallMessage });
    return list;
  }, [selected, perPackage, overall, catalog]);

  useEffect(() => {
    // Nothing is priced while the current plan is unknown: the builder is not
    // even rendered in that state, and a preview issued from it would produce a
    // total for a plan nobody chose.
    if (loading || loadError) return undefined;
    // A discount the server would refuse must never be quietly dropped and the
    // resulting list-price total shown as if it were the quote. Show nothing.
    if (inputErrors.length) {
      setPreview(null);
      setPreviewError(null);
      setPreviewing(false);
      return undefined;
    }
    let cancelled = false;
    setPreviewing(true);
    const timer = setTimeout(async () => {
      try {
        // `compareToActive` makes the SERVER produce the before → after: it
        // composes the assignment in force from the same catalog read as this
        // plan, so the two numbers in the confirmation are commensurable and a
        // price edited between them cannot masquerade as a change the operator
        // made.
        const result = await billingApi.previewPlanAssignment(tenantId, body, { compareToActive: true });
        if (cancelled) return;
        setPreview(result);
        setPreviewedKey(bodyKey);
        setPreviewError(null);
      } catch (e: any) {
        if (cancelled) return;
        setPreview(null);
        setPreviewError(e?.message || 'This plan could not be priced.');
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [tenantId, bodyKey, body, loading, loadError, inputErrors.length]);

  /** The totals on screen were computed from a plan that is no longer the plan on screen. */
  const stale = !preview || previewedKey !== bodyKey;

  /**
   * An acknowledgement is only ever valid for the comparison it was given
   * against, so ANY change to the plan withdraws it. Nothing about "the
   * operator clicked a box once" should survive them editing the price after.
   */
  useEffect(() => { setAcknowledged(false); }, [bodyKey]);

  /**
   * The server's own before → after, valid ONLY while the preview still matches
   * the plan on screen. Undefined the moment either goes stale, because a
   * comparison against a plan nobody is looking at is worse than none.
   */
  const summary: PlanChangeSummary | undefined = !stale ? preview?.changeSummary : undefined;
  /** The server could not price the plan in force — so the "before" is UNKNOWN, never zero. */
  const previewBeforeError = !stale ? preview?.beforeError : undefined;
  /**
   * SOMEONE ELSE ACTIVATED A PLAN WHILE THIS ONE WAS OPEN.
   *
   * The load-time read and the server's "what is in force right now" no longer
   * name the same assignment, so the comparison on screen is against a plan
   * that has already been superseded. Publishing anyway would still be SAFE —
   * the activation transaction refuses on a changed prior — but it would be
   * approved against the wrong "before", which is the one thing this gate
   * exists to prevent.
   */
  const priorBeforeMoved = !!summary
    && !previewBeforeError
    && (preview?.before?.assignmentId ?? null) !== (active?.assignmentId ?? null);

  /**
   * A placement can only be edited once the server has told us which locations
   * exist and in WHAT ORDER — "the first location" a bills-once package lands on
   * is defined by that order, so materialising a placement from a guess could
   * move a charge to a different store.
   *
   * Declared HERE, above every handler that reads it, because it now gates the
   * ADD control as well as the per-package switches: `addPackage`'s store-scoped
   * branch needs `preview.locationIds`, and without it the add fell through to
   * the plan-wide branch and quietly billed every store — under a screen that
   * promises the opposite.
   */
  const canPlace = !!focusLocationId && !!preview && !stale;

  /**
   * WOULD PUBLISHING THIS CHANGE ANYTHING?
   *
   * Reachable the moment the indeterminate path started reloading: the reload
   * clears `draftId`, so the obvious "try again" mints a NEW draft over a plan
   * that may already be live — which activates cleanly, supersedes an identical
   * record and fires the notifications, all to change nothing.
   *
   * Answered from the SERVER's own comparison (`changeSummary.hasChange`) plus
   * the SERVER's own two assignment records, never from anything reassembled
   * here. See `publishNoOp` for why "same money, different record" is allowed
   * while "identical" is refused.
   */
  const noOp = !stale && preview ? publishNoOp({ preview, activeAssignment: active }) : null;

  /** The server did not confirm the overall discount is already inside the totals. */
  const unverified = !!preview && overallDiscountUnverified(preview);
  const composeErrors = preview?.errors ?? [];
  /**
   * Errors on the plan actually IN FORCE, not the one being edited.
   *
   * `planAssignment.ts`'s preview returns compose errors inside a 200, so a
   * tenant whose active, sold plan contains a package with no SKU shows a
   * clean "Packages billing today" figure with no sign that a package they
   * were sold cannot be priced. The figure itself is still correct — an
   * unpriceable package is not billed — but this is the one screen that
   * exists to say what the tenant was sold, so it must not stay silent about
   * a broken line on the plan they are already paying for.
   */
  const currentErrors = current?.errors ?? [];
  const errorByPackage = useMemo(
    () => Object.fromEntries(composeErrors.map((e) => [e.packageId, e.reason])),
    [composeErrors],
  );

  const blockedReason = (() => {
    // D12: nobody's bill changes without a human seeing BOTH numbers. If the
    // assignment in force could not be composed, the "before" is unknown and
    // there is nothing to compare the new price against.
    if (active && !current) {
      return 'The plan in force could not be priced, so there is nothing to compare this against. Reload before changing what this tenant pays.';
    }
    if (previewBeforeError) {
      return `The plan in force could not be priced (${previewBeforeError}), so there is no "before" to compare this against.`;
    }
    if (priorBeforeMoved) {
      return 'This tenant\'s plan changed in another session while this was open, so the comparison below is against a plan that is no longer in force. Reload before publishing.';
    }
    if (inputErrors.length) return 'Fix the discount(s) flagged below first.';
    if (previewError) return 'This plan could not be priced, so there is no amount to approve.';
    if (previewing || stale) {
      return 'Waiting for the priced total — nothing is approved until the amount on screen is the amount for this plan.';
    }
    if (unverified) {
      return 'The overall discount cannot be accounted for, so the amount on screen is not one anybody can stand behind.';
    }
    if (composeErrors.length) {
      return `${composeErrors.length} package(s) cannot be priced — activation is refused until every one is fixed.`;
    }
    // A publish that changes NOTHING is refused rather than warned about. It
    // supersedes a commercial record, churns the subscription and emails the
    // tenant, all to say nothing happened — and the retry after a publish whose
    // response never came back lands here by default.
    if (noOp?.kind === 'identical') return noOp.reason;
    return null;
  })();

  // -- editing --------------------------------------------------------------
  const addPackage = (key: string | null) => {
    if (!key || selected.includes(key)) return;
    // THE PROMISE THIS SCREEN MAKES: "a package added here is placed at this
    // store". It can only be kept while the placement is editable — which needs
    // the server's location list. Without it the add would fall through to the
    // plan-wide branch below and bill EVERY store, silently, from a screen
    // headed with one store's name. So it is refused rather than downgraded.
    // The control is disabled for the same reason; this is the second lock.
    if (focusLocationId && !canPlace) return;
    setSelected((prev) => [...prev, key]);
    setPerPackage((prev) => ({ ...prev, [key]: { ...BLANK_DISCOUNT } }));

    // In the LOCATION editor a package is added FOR THIS STORE. Adding it
    // everywhere would silently raise every other store's bill from a screen
    // headed with one store's name — the operator asked to change this
    // location's price, not the tenant's. The plan-wide add stays where it
    // belongs, on the Plan panel, and the editor says so.
    if (focusLocationId && preview) {
      setPlacement((prev) => togglePlacement(
        prev ?? materialisePlacement(selected, preview.locationIds),
        {
          locationId: focusLocationId,
          packageId: key,
          enabled: true,
          scope: catalog.get(key)?.scope === 'tenant' ? 'tenant' : 'location',
        },
      ));
      return;
    }

    setPlacement((prev) => {
      if (!prev) return prev;
      // With an explicit placement in force a new package lands NOWHERE unless
      // it is placed, which under-bills silently. Default it to the locations
      // the rest of this plan already bills at; `placed_nowhere` still calls it
      // out if there are none.
      const billing = Object.keys(prev).filter((locationId) => (prev[locationId] || []).length > 0);
      if (!billing.length) return prev;
      const next = { ...prev };
      for (const locationId of billing) next[locationId] = [...(next[locationId] || []), key];
      return next;
    });
  };

  const removePackage = (key: string) => {
    setSelected((prev) => prev.filter((k) => k !== key));
    // The discount goes WITH the package. A per-package discount naming a
    // package that is not in `planPackageIds` is refused with a 400, and a draft
    // replace merges the stored discounts, so a discount left behind here would
    // make the save fail — `buildPlanDraftBody` trims it too, belt and braces.
    setPerPackage((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setPlacement((prev) => {
      if (!prev) return prev;
      const next: Record<string, string[]> = {};
      for (const locationId of Object.keys(prev)) {
        next[locationId] = (prev[locationId] || []).filter((id) => id !== key);
      }
      return next;
    });
  };

  const setDiscount = (key: string, patch: Partial<DiscountInput>) => {
    setPerPackage((prev) => ({ ...prev, [key]: { ...BLANK_DISCOUNT, ...prev[key], ...patch } }));
  };

  /**
   * "Adjust the final subscription price" — the operator's actual question,
   * which the model answers with a discount.
   *
   * This SETS THE OVERALL DISCOUNT so the composed total lands on the figure
   * typed. It does not display that figure: the preview re-runs and the number
   * on screen is still the server's, so a wrong conversion would be visible
   * before publishing rather than after. `overallDiscountForTarget` is exact —
   * integer arithmetic, and `apportionCents` guarantees the shares sum to the
   * discount — so the total lands on the target to the cent.
   *
   * Requires a fresh preview, because the reversal needs the server's own
   * `totals`. Refused, with the reason, when the target is above what the plan
   * can bill: a discount cannot raise a price, and silently clamping it would
   * leave an operator staring at a number they did not type.
   */
  const applyTargetPrice = () => {
    setTargetError(null);
    if (!preview || stale) {
      setTargetError('Wait for the priced total — the target is worked out from it.');
      return;
    }
    const raw = targetPrice.trim();
    if (!raw) { setTargetError('Enter a monthly amount.'); return; }
    const dollars = Number(raw);
    if (!Number.isFinite(dollars)) { setTargetError('Enter a number.'); return; }
    const result = overallDiscountForTarget(preview, Math.round(dollars * 100), overall.reason);
    if (result.error) { setTargetError(result.error); return; }
    setOverall(result.discount!);
  };

  // -- saving / activating --------------------------------------------------
  const saveDraft = async (): Promise<string> => {
    // The ONE case a replace cannot express: the stored draft has an explicit
    // placement and this plan no longer does. The merge would keep the stored
    // one, so the saved plan would compose to a DIFFERENT number than the one
    // just previewed and approved. Mint a fresh draft instead — an abandoned
    // draft costs nothing; a plan that bills differently than it was quoted is
    // the exact failure this screen exists to prevent.
    const canReplace = !!draftId && !(draftHasPlacement && !placement);
    const saved = await billingApi.savePlanAssignmentDraft(
      tenantId,
      canReplace ? { ...body, assignmentId: draftId! } : body,
    );
    setDraftId(saved.assignment.assignmentId);
    setDraftHasPlacement(!!saved.assignment.locationPackageIds);
    return saved.assignment.assignmentId;
  };

  /**
   * A draft id the server has just told us is unusable — it was activated,
   * superseded or removed under us. Forget it, so the next save mints a new
   * draft instead of retrying the same 409 forever.
   */
  const forgetDraftIfNamed = (message: string, id?: string | null) => {
    // `id` matters when a draft was just minted in this same handler: the
    // `draftId` in scope is still the pre-render value, so without it a failure
    // naming the NEW draft would not be recognised.
    const target = id || draftId;
    if (target && message.includes(target)) {
      setDraftId(null);
      setDraftHasPlacement(false);
    }
  };

  const onSaveDraft = async () => {
    setSaving(true);
    try {
      await saveDraft();
      toastService.success('Draft saved. Nothing is billed until it is activated.');
    } catch (e: any) {
      const message = e?.message || 'Could not save this draft.';
      forgetDraftIfNamed(message);
      toastService.error(message);
    } finally {
      setSaving(false);
    }
  };

  const onActivate = async () => {
    setActivating(true);
    setRefusal(null);
    const quoted = preview ? payableMonthlyCents(preview) : null;
    let savedId: string | null = null;
    try {
      savedId = await saveDraft();
      const result = await billingApi.activatePlanAssignment(tenantId, savedId);
      // Activation RE-COMPOSES server-side. If the catalog moved while this
      // quote was open, the amount now live is not the amount that was approved
      // — say so loudly rather than let the operator believe the number they
      // last saw is the number being charged.
      const charged = payableMonthlyCents(result);
      // The draft is now ACTIVE. Saving over it would 409 ("is never edited"),
      // so the next save must mint a new one.
      setDraftId(null);
      setDraftHasPlacement(false);
      setConfirmOpen(false);
      setAcknowledged(false);

      /**
       * WHAT TO TELL THE OPERATOR.
       *
       * A 200 means the assignment was RECORDED. It does not mean the money
       * moved: billing-service runs the Stripe sync after the transaction, and
       * it can fail on its own. Saying "price changed" on the 200 alone would
       * tell an operator something that is not yet true — and the customer has
       * deliberately NOT been emailed in that case, which they also have to
       * know. `publishReport` is the single place that reads all of it.
       */
      const report = publishReport(result);
      setLastPublish(report);

      if (quoted !== null && quoted !== charged) {
        toastService.warning(
          `Activated at ${money(charged)}/mo — not the ${money(quoted)}/mo previewed. The catalog changed while this quote was open; review the plan.`,
          15000,
        );
      } else if (!report.moneyMoved) {
        toastService.warning(
          `Recorded at ${money(charged)}/mo, but the subscription was NOT updated: ${report.problem}`,
          20000,
        );
      } else {
        toastService.success(
          `Published — ${money(charged)}/mo across ${result.perLocation.length} location${result.perLocation.length === 1 ? '' : 's'}, live now with proration.${
            report.customerNotified ? ' The customer has been emailed what changed.' : ''
          }`,
        );
      }
      await load();
      await onActivated();
    } catch (e: any) {
      if (isPlanActivationRefusal(e)) {
        setRefusal(e.refusal.errors);
        toastService.error('Activation refused — some packages cannot be priced. Nothing changed.', 12000);
      } else if (isIndeterminateActivation(e)) {
        /**
         * THE PUBLISH REQUEST NEVER CAME BACK.
         *
         * `activate` reads and writes one Stripe subscription per location, so a
         * large estate can run past API Gateway's 29s ceiling. The gateway kills
         * the RESPONSE — the Lambda carries on and may well finish — and a bare
         * "billing 504" toast would leave an operator with a network error over a
         * change that is quite possibly live and half-synced.
         *
         * So: the persistent note says we cannot confirm, and the screen RELOADS,
         * which is what actually answers the question — a reload shows whether
         * the new assignment is now the active one. The dialog is closed either
         * way, because leaving it open invites a retry that would be refused with
         * "already active" and read as a second failure.
         */
        const message = e?.message || 'The publish request did not complete.';
        setLastPublish(indeterminatePublishReport(message));
        setConfirmOpen(false);
        setAcknowledged(false);
        toastService.error(`${message} Reloading — check what is in force before retrying.`, 20000);
        await load();
        await onActivated();
      } else {
        const message = e?.message || 'Could not activate this plan.';
        forgetDraftIfNamed(message, savedId);
        toastService.error(message);
      }
    } finally {
      setActivating(false);
    }
  };

  // -- derived display ------------------------------------------------------
  /** Per package: the SUM of the server's own line nets, and WHERE they landed. */
  const lineSummary = useMemo(() => {
    const map: Record<string, {
      listCents: number; netCents: number; locationIds: string[]; discount?: PlanDiscount;
    }> = {};
    for (const location of preview?.perLocation ?? []) {
      for (const item of location.items) {
        const row = map[item.packageId] || { listCents: 0, netCents: 0, locationIds: [] };
        row.listCents += item.listCents;
        row.netCents += item.netCents;
        row.locationIds.push(location.locationId);
        row.discount = item.discount;
        map[item.packageId] = row;
      }
    }
    return map;
  }, [preview]);

  /**
   * What composition bills TODAY: the active assignment's net, or zero when
   * there is none — because with no assignment there is no managed line and
   * `subscriptionSync` skips the tenant entirely (D12). Zero here is a fact,
   * not a missing number, and the base subscriptions are shown beside it so it
   * never reads as "this tenant pays nothing".
   */
  const composedTodayCents = summary
    ? summary.beforeTotalCents
    : current ? payableMonthlyCents(current) : 0;
  /**
   * FALSE when there IS an active assignment but it could not be composed. Zero
   * would then be a lie about the tenant's current bill, and the D12 rule —
   * nobody's bill changes without a human seeing both numbers — cannot be
   * satisfied, so activation is blocked rather than shown against a wrong
   * "before".
   */
  const composedTodayKnown = (!active || !!current) && !previewBeforeError;
  // `!unverified` too: the Totals card above says "no total is shown" the
  // moment the overall discount cannot be accounted for (either overstated or
  // double-discounted) — the bold "After activating" figure and the
  // per-location breakdown must not go on quoting one anyway. Unreachable
  // today (both handlers hardcode `subscriptionDiscountApplied: true`, and
  // activation is separately blocked by `blockedReason`), but the gate should
  // say what it does rather than rely on that.
  const composedCents = preview && !stale && !unverified ? payableMonthlyCents(preview) : null;
  const delta = summary
    ? summary.deltaCents
    : composedCents === null || !composedTodayKnown ? null : composedCents - composedTodayCents;

  // -- location focus -------------------------------------------------------
  /** The composed lines for the store this editor is scoped to, if any. */
  const focusLine = focusLocationId
    ? preview?.perLocation.find((l) => l.locationId === focusLocationId) ?? null
    : null;
  const focusNetCents = focusLine
    ? focusLine.items.reduce((sum, item) => sum + item.netCents, 0)
    : 0;
  const focusChange = focusLocationId
    ? summary?.locationChanges.find((l) => l.locationId === focusLocationId) ?? null
    : null;
  /** What this store is charged for each package — the server's own line, never a share of a total. */
  const focusByPackage = useMemo(
    () => Object.fromEntries((focusLine?.items ?? []).map((item) => [item.packageId, item])),
    [focusLine],
  );

  /**
   * THE CHANGE AS THIS STORE SEES IT.
   *
   * `summary.changes` sums each package across EVERY location, so on a
   * multi-store tenant those figures are the whole agreement's, not this
   * store's. Under a dialog headed "Publish this price for Downtown?" that is
   * the wrong number beside the operator's consent. `locationChangeRows`
   * re-cuts the same server-composed lines for one location — a lookup of
   * `netCents`, never a share of a total divided up here — and keeps the
   * tenant-wide row on each entry so both can be shown, each labelled for what
   * it is.
   */
  const focusRows: LocationPackageChange[] | null = useMemo(() => {
    if (!focusLocationId || !summary || !preview) return null;
    return locationChangeRows({
      locationId: focusLocationId,
      before: preview.before?.perLocation,
      after: preview.perLocation,
      changes: summary.changes,
    });
  }, [focusLocationId, summary, preview]);

  /** This store's before → after, both from the server. Null until the comparison is ready. */
  const focusBeforeCents = focusChange ? focusChange.beforeCents : null;
  const focusAfterCents = preview && !stale ? focusNetCents : null;

  const setBillsHere = (key: string, enabled: boolean) => {
    if (!focusLocationId || !preview) return;
    const scope = catalog.get(key)?.scope === 'tenant' ? 'tenant' : 'location';
    setPlacement((prev) => togglePlacement(
      // No explicit placement yet means the plan bills by the scope rule.
      // Materialising it first is provably a no-op on every other store's
      // amount — see `materialisePlacement` — so one store's toggle can never
      // silently reprice the rest.
      prev ?? materialisePlacement(selected, preview.locationIds),
      { locationId: focusLocationId, packageId: key, enabled, scope },
    ));
  };

  return (
    <Card>
      <CardHeader
        icon={focusLocationId ? <Store size={15} /> : <ClipboardList size={15} />}
        title={focusLocationId
          ? `Change price — ${focusLocationName || focusLocationId}`
          : 'Plan & pricing'}
        hint={headerHint || (focusLocationId
          ? 'what this client pays, package by package, at this store'
          : 'what this tenant was sold — list · discount · net')}
        actions={
          <button
            onClick={load}
            disabled={loading}
            className="px-3.5 py-2 rounded-xl border border-stone-300 text-stone-700 text-xs font-semibold hover:bg-stone-50 disabled:opacity-40 min-h-[44px] inline-flex items-center gap-1.5"
          >
            {loading ? <RubyGemMark size={13} drawing color="currentColor" /> : <RefreshCw size={13} />} Reload
          </button>
        }
      />

      <div className="p-4 space-y-4 text-stone-800">
        {loading ? (
          <RubyLoader variant="section" className="py-10" />
        ) : loadError ? (
          <Note tone="danger">
            <strong>The current plan could not be read.</strong> {loadError} That is not the same as this tenant
            having no plan — nothing is shown, and nothing should be activated, until this read succeeds.
          </Note>
        ) : (
          <>
            {/* What the last publish actually did ---------------------------
                Persistent, because "the plan was recorded but Stripe was not
                updated" is the one outcome an operator must not miss, and a
                toast that has faded is exactly how they miss it. */}
            {lastPublish && !lastPublish.moneyMoved && (
              <Note tone="danger">
                <strong>
                  {lastPublish.indeterminate
                    ? 'We do not know whether this was published.'
                    : 'The plan was recorded, but the subscription was NOT updated.'}
                </strong>{' '}
                {lastPublish.problem}{' '}
                {/* Only claim the flag when the sync actually recorded a failure.
                    `billingSyncPending` is written on the ERROR path; a publish
                    that never came back may not have reached it at all. */}
                {!lastPublish.indeterminate && 'Retry the sync from the Plan panel. '}
                {customerNotifiedSentence(lastPublish)}
                {' '}{adminNotifiedSentence(lastPublish)}
              </Note>
            )}
            {lastPublish?.moneyMoved && (
              <Note tone="success">
                <strong>Published.</strong> The subscription was updated immediately, prorated for the rest
                of the current period.{' '}
                {customerNotifiedSentence(lastPublish)}
                {' '}{adminNotifiedSentence(lastPublish)}
              </Note>
            )}

            {/* Nothing to publish ------------------------------------------
                `identical` also fills `blockedReason`, so the button is dead;
                this says why, because a disabled button with the explanation
                only in a tooltip is how an operator concludes the screen is
                broken and goes looking for another way in. */}
            {noOp?.kind === 'identical' && (
              <Note tone="warn">
                <strong>Nothing to publish.</strong> {noOp.reason}
                {lastPublish?.indeterminate
                  ? ' That answers the question above: this plan IS in force, so the publish you were unsure about did land.'
                  : ' Change something, or close this — the plan in force already says this.'}
              </Note>
            )}
            {noOp?.kind === 'same-money' && (
              <Note tone="info">
                <strong>The money does not move.</strong> {noOp.reason}
              </Note>
            )}

            {/* The location this editor is scoped to ------------------------ */}
            {focusLocationId && (
              <Note tone="info">
                <strong>This is {focusLocationName || focusLocationId}.</strong> Turning a package on or off
                below changes it <em>for this store only</em>. A discount, by contrast, belongs to the
                agreement and applies wherever that package bills — the per-store effect is shown on each
                row. A package added here is placed at this store; to sell one to every store, use the
                Plan tab.
              </Note>
            )}

            {/* Where this tenant stands ------------------------------------ */}
            {active ? (
              <Note tone="success">
                <strong>Active plan assignment.</strong> {active.planPackageIds.length} package(s), effective{' '}
                {shortDate(active.effectiveFrom)}, recorded by {active.createdBy}.
                {currentError && <> Its price could not be recomputed: {currentError}</>}
              </Note>
            ) : (
              <Note tone="info">
                <strong>No plan assignment yet.</strong> This tenant is on legacy flat pricing, so no package
                composition exists for it. Activating one here becomes the record of what it was sold, and the
                source of every future price change.
              </Note>
            )}

            {extraActive.length > 0 && (
              <Note tone="danger">
                <strong>{extraActive.length + 1} assignments are active at once.</strong> Exactly one should be.
                The newest ({active?.assignmentId}) is the one shown; activating a new plan supersedes all of them
                in a single transaction.
              </Note>
            )}

            {resumable && resumable.assignmentId !== draftId && (
              <div className="rounded-xl border border-stone-200 bg-stone-50 text-stone-800 px-3.5 py-2.5 flex items-center justify-between gap-3 flex-wrap text-xs">
                <span>
                  An unactivated draft from {shortDate(resumable.createdAt)} by {resumable.createdBy} exists
                  ({resumable.planPackageIds.length} package(s)). Nothing is billed from a draft.
                </span>
                <button
                  onClick={() => seedFrom(resumable)}
                  className="px-3.5 py-2 rounded-xl border border-stone-300 bg-white text-stone-700 font-semibold hover:bg-stone-100 min-h-[44px]"
                >
                  Load that draft
                </button>
              </div>
            )}

            {/* Packages ---------------------------------------------------- */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h4 className="text-sm font-semibold text-stone-800">Packages on this plan</h4>
                <div className="w-full sm:w-72">
                  {/* In the location editor the add is gated on the placement
                      being editable. Adding while the preview is missing — the
                      moment the modal opens, and indefinitely after a preview
                      error — would place the package NOWHERE explicit and bill
                      it at every store by scope, which is the opposite of what
                      the copy under this control promises. */}
                  <SearchableDropdown
                    options={addOptions}
                    value={null}
                    onChange={addPackage}
                    placeholder={
                      focusLocationId && !canPlace
                        ? 'Waiting for prices before a package can be added here…'
                        : addOptions.length ? 'Add a package…' : 'No further packages in the catalog'
                    }
                    searchPlaceholder="Search the catalog…"
                    disabled={!addOptions.length || (!!focusLocationId && !canPlace)}
                    variant="light"
                  />
                </div>
              </div>

              {selected.length === 0 ? (
                <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 text-stone-600 px-4 py-6 text-center text-sm">
                  No packages yet. A plan with none bills nothing — and, once activated, supersedes whatever came
                  before it.
                </div>
              ) : (
                <div className="space-y-2">
                  {selected.map((key) => {
                    const option = catalog.get(key);
                    const input = perPackage[key] || BLANK_DISCOUNT;
                    const line = lineSummary[key];
                    const composeError = errorByPackage[key];
                    const ambiguous = (option?.skuIds.length ?? 0) > 1;
                    /** This store's own composed line for this package, when it has one. */
                    const here = focusByPackage[key];
                    /**
                     * WHERE THIS PACKAGE ACTUALLY BILLS — from the composition,
                     * never from the placement list.
                     *
                     * A bills-once package materialised into a placement is
                     * LISTED at every location and BILLS at one, so reading the
                     * placement would show it enabled everywhere and the switch
                     * would appear to do nothing. The composition is the truth.
                     */
                    const billsAt = (line?.locationIds ?? []).map((id) => locationNames[id] || id);
                    const free = option?.amountCents === 0;
                    return (
                      <div
                        key={key}
                        className={`rounded-xl border p-3 text-stone-800 ${
                          composeError ? 'border-red-300 bg-red-50' : 'border-stone-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-[12rem] flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-sm text-stone-900">{option?.name || key}</span>
                              <Chip tone={option?.scope === 'tenant' ? 'violet' : 'blue'}>
                                {option?.scope === 'tenant'
                                  ? <><Building2 size={10} /> Billed once</>
                                  : <><Store size={10} /> Per store</>}
                              </Chip>
                              {option?.deprecated && <Chip tone="amber">Deprecated</Chip>}
                              {!option && <Chip tone="red">Not in the catalog</Chip>}
                            </div>
                            <p className="text-[11px] text-stone-500 font-mono mt-0.5">{key}</p>
                            {/* WHERE IT BILLS. §14/C2: "billed once" is not an
                                answer to "billed once WHERE" — and a placement
                                nobody can see is a placement nobody can repair. */}
                            {preview && (
                              <p className="text-[11px] text-stone-600 mt-0.5 flex items-start gap-1">
                                <MapPin size={11} className="mt-0.5 shrink-0 text-stone-400" />
                                <span>
                                  {billsAt.length
                                    ? <>Billed at <strong>{billsAt.join(', ')}</strong></>
                                    : free
                                      ? 'Free — included everywhere, bills nothing'
                                      : <span className="text-amber-700">Billed at no location</span>}
                                </span>
                              </p>
                            )}
                          </div>

                          <div className="text-right shrink-0 min-w-[6rem]">
                            <p className="text-[10px] uppercase tracking-wider text-stone-500">List</p>
                            {/* Once priced, this is the server's OWN summed list for this
                                package, so List and Billed are the same kind of number and
                                the discount is visibly the difference between them. Before
                                the first preview it falls back to the catalog price — but a
                                package absent from the catalog with no line yet has no price
                                to fall back to, and must read "—", not $0.00, matching the
                                dash treatment "This store pays" already uses below. */}
                            <p className="text-sm font-semibold tabular-nums text-stone-800">
                              {line || option ? (
                                <>
                                  {money(line ? line.listCents : option!.amountCents)}
                                  <span className="text-stone-400 font-normal">/mo</span>
                                </>
                              ) : (
                                <span className="text-stone-400 font-normal">—</span>
                              )}
                            </p>
                            {line && line.locationIds.length > 1 && (
                              <p className="text-[11px] text-stone-500">
                                {money(option?.amountCents ?? 0)} × {line.locationIds.length}
                              </p>
                            )}
                          </div>

                          {/* THE ANSWER TO "what is this client paying for this?".
                              In the location editor that question is about ONE store, so this
                              store's own line net leads and the plan-wide sum sits beneath it.
                              Both are the server's numbers; neither is a share of a total this
                              screen divided up. */}
                          {focusLocationId ? (
                            <div className="text-right shrink-0 min-w-[7rem]">
                              <p className="text-[10px] uppercase tracking-wider text-stone-500">
                                This store pays
                              </p>
                              <p className="text-sm font-semibold tabular-nums text-stone-900">
                                {here
                                  ? money(here.netCents)
                                  : <span className="text-stone-400 font-normal">—</span>}
                              </p>
                              <p className="text-[11px] text-stone-500">
                                {here
                                  ? (line && line.locationIds.length > 1
                                    ? `${money(line.netCents)} across ${line.locationIds.length} stores`
                                    : 'the whole of this package')
                                  : composeError
                                    ? 'cannot be priced'
                                    : !preview
                                      ? 'not priced yet'
                                      : option?.amountCents === 0
                                        ? 'free — adds no line anywhere'
                                        : line
                                          ? `not billed here · ${money(line.netCents)} elsewhere`
                                          : 'bills nowhere'}
                              </p>
                            </div>
                          ) : (
                            <div className="text-right shrink-0 min-w-[7rem]">
                              <p className="text-[10px] uppercase tracking-wider text-stone-500">Billed</p>
                              {/* The SUM of the server's own line nets for this package — every
                                  reduction, including its share of the overall discount, is already
                                  inside them. Nothing here recomputes a discount. */}
                              <p className="text-sm font-semibold tabular-nums text-stone-900">
                                {line ? money(line.netCents) : <span className="text-stone-400 font-normal">—</span>}
                              </p>
                              <p className="text-[11px] text-stone-500">
                                {line
                                  ? `at ${line.locationIds.length} location${line.locationIds.length === 1 ? '' : 's'}`
                                  : composeError
                                    ? 'cannot be priced'
                                    : !preview
                                      ? 'not priced yet'
                                      : option?.amountCents === 0
                                        ? 'free — adds no line'
                                        : 'bills nowhere'}
                              </p>
                            </div>
                          )}

                          <button
                            onClick={() => removePackage(key)}
                            aria-label={focusLocationId
                              ? `Remove ${option?.name || key} from the plan entirely`
                              : `Remove ${option?.name || key}`}
                            title={focusLocationId
                              ? 'Removes this package from the plan for EVERY store. To stop billing it at this store only, use “Stop billing here”.'
                              : undefined}
                            className="p-2.5 rounded-xl border border-stone-300 text-stone-500 hover:text-red-600 hover:border-red-300 min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>

                        {/* BILLED HERE — the per-store switch (spec §14/C2).
                            It edits the assignment's `locationPackageIds`, which is
                            the only per-location lever the model has. A bills-once
                            package MOVES rather than doubling; `togglePlacement`
                            makes that structurally impossible to get wrong. */}
                        {focusLocationId && (
                          <div className="mt-2.5 flex items-center justify-between gap-3 flex-wrap rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
                            <span className="text-xs text-stone-700">
                              {free
                                ? 'Free in the catalog, so it bills nothing at any store — there is nothing to switch off.'
                                : here
                                  ? <>Billed at <strong>{focusLocationName || focusLocationId}</strong>.</>
                                  : <>Not billed at <strong>{focusLocationName || focusLocationId}</strong>.</>}
                              {!free && option?.scope === 'tenant' && (
                                <span className="text-stone-500">
                                  {' '}This package bills once for the whole tenant, so switching it on here
                                  moves it — it can never be charged twice.
                                </span>
                              )}
                            </span>
                            <button
                              onClick={() => setBillsHere(key, !here)}
                              disabled={!canPlace || free}
                              className={`px-3.5 py-2 rounded-xl text-xs font-semibold min-h-[44px] disabled:opacity-40 disabled:cursor-not-allowed ${
                                here
                                  ? 'border border-stone-300 text-stone-700 hover:bg-stone-100'
                                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
                              }`}
                            >
                              {here ? 'Stop billing here' : 'Bill here'}
                            </button>
                          </div>
                        )}

                        {/* Discount — one mode, never two. A body carrying both
                            percentOff and amountOffCents is refused by the schema,
                            and this control cannot produce one. Shared with the
                            Deals builder, which writes the same shape. */}
                        <DiscountFields
                          label="Discount"
                          rowClassName="mt-2.5 flex items-end gap-2 flex-wrap"
                          value={input}
                          onChange={(patch) => setDiscount(key, patch)}
                        />

                        {composeError && (
                          <p className="mt-2 text-xs text-red-800 flex items-start gap-1.5">
                            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                            <span><strong>{key}</strong> cannot be priced: {composeError}</span>
                          </p>
                        )}
                        {ambiguous && !composeError && (
                          <p className="mt-2 text-xs text-amber-700">
                            {option?.skuIds.length} catalog SKUs claim this package ({option?.skuIds.join(', ')}).
                            The catalog must name exactly one price.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Overall discount -------------------------------------------- */}
            <div className="rounded-xl border border-stone-200 bg-stone-50 text-stone-800 p-3">
              {/* The SAME control as every other discount on the platform.
                  It was a hand-written copy differing only in a column width,
                  two placeholders and the label — knobs `DiscountFields`
                  already exposes — which is exactly the divergence risk the
                  shared component exists to remove.
                  One rendering difference, deliberate: the validation error now
                  sits directly under the fields (where every other
                  `DiscountFields` on this screen puts it) rather than below the
                  sentence about how the discount is spread. Same element, same
                  classes, same text. */}
              <DiscountFields
                label="Overall discount"
                modeWidthClass="w-40"
                percentPlaceholder="5"
                amountPlaceholder="50.00"
                reasonPlaceholder="e.g. launch pricing, 12 months"
                value={overall}
                onChange={(patch) => setOverall((prev) => ({ ...prev, ...patch }))}
              />
              <p className="text-[11px] text-stone-500 mt-2">
                Spread across every line in proportion to its size, so each package's share of it is recorded — and
                already inside the “Billed” figures above.
              </p>

              {/* "Make it $X a month" — the operator's actual question.
                  It SETS the overall discount above; it never displays a price.
                  The figure on screen still comes back from billing-service, so
                  the number approved is always the server's own. */}
              <div className="mt-3 pt-3 border-t border-stone-200 flex items-end gap-2 flex-wrap">
                <div className="w-40">
                  <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1">
                    Or set the final monthly
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={targetPrice}
                    onChange={(e) => { setTargetPrice(e.target.value); setTargetError(null); }}
                    placeholder={preview && !stale ? (payableMonthlyCents(preview) / 100).toFixed(2) : '0.00'}
                    className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-xl text-stone-800 placeholder-stone-400 text-sm min-h-[44px]"
                  />
                </div>
                <button
                  onClick={applyTargetPrice}
                  disabled={!preview || stale || !targetPrice.trim()}
                  className="px-4 py-3 rounded-xl border border-stone-300 bg-white text-stone-700 text-xs font-semibold hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
                >
                  Work out the discount
                </button>
                <p className="text-[11px] text-stone-500 flex-1 min-w-[12rem]">
                  Sets the overall discount so this plan composes to exactly that figure
                  {preview && !stale && <> — at most {money(preOverallCents(preview))}/mo before any overall discount</>}.
                  {focusLocationId && ' It is the TENANT total across every store, not this store alone; each store’s share moves in proportion.'}
                </p>
              </div>
              {targetError && (
                <p className="mt-2 text-xs text-red-700 flex items-start gap-1.5">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {targetError}
                </p>
              )}
            </div>

            {/* Placement --------------------------------------------------- */}
            {placement && (
              <Note tone="info">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <span>
                    <strong>This plan places packages at specific locations.</strong> Carried forward from what the
                    tenant was sold — a free-location quota, or packages bought for some stores and not others. Any
                    package placed nowhere is called out below.
                  </span>
                  <button
                    onClick={() => setPlacement(null)}
                    className="px-3.5 py-2 rounded-xl border border-blue-300 bg-white text-blue-800 text-xs font-semibold hover:bg-blue-100 min-h-[44px] whitespace-nowrap"
                  >
                    Bill by scope instead
                  </button>
                </div>
              </Note>
            )}

            {/* Totals ------------------------------------------------------ */}
            <div className="rounded-2xl border border-stone-200 bg-white text-stone-800 p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h4 className="text-sm font-semibold text-stone-800">This plan, priced by billing-service</h4>
                {previewing && (
                  <span className="text-xs text-stone-500 inline-flex items-center gap-1.5">
                    <RubyGemMark size={12} drawing color="currentColor" /> Pricing…
                  </span>
                )}
              </div>

              {inputErrors.length > 0 ? (
                <Note tone="danger">
                  <strong>Not priced.</strong> {inputErrors.map((e) => `${e.label}: ${e.message}`).join(' ')} A
                  discount the billing service would refuse is never quietly ignored — no total is shown until it
                  is fixed.
                </Note>
              ) : previewError ? (
                <Note tone="danger">
                  <strong>This plan could not be priced.</strong> {previewError} No amount is shown, because an
                  amount that did not come from billing-service is not the amount that would be charged.
                </Note>
              ) : unverified ? (
                // No number at all. Both readings of this response are wrong in
                // a different direction, so showing one and captioning it would
                // still be showing a figure nobody can stand behind.
                <Note tone="danger">
                  <strong>The overall discount cannot be accounted for, so no total is shown.</strong>{' '}
                  billing-service returned an overall discount without confirming it is already inside the total,
                  which leaves the amount either overstated or double-discounted. Reload; nothing may be activated
                  from this.
                </Note>
              ) : (
                <div className={`grid grid-cols-3 gap-3 ${stale ? 'opacity-40' : ''}`}>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-stone-500">List</p>
                    <p className="text-lg font-bold tabular-nums text-stone-800">
                      {preview ? money(preview.totals.listCents) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-stone-500">Discount</p>
                    <p className="text-lg font-bold tabular-nums text-amber-600">
                      {preview ? `−${money(preview.totals.discountCents)}` : '—'}
                    </p>
                    {!!preview?.totals.overallDiscountCents && (
                      <p className="text-[11px] text-stone-500">
                        incl. {money(preview.totals.overallDiscountCents)} overall
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-stone-500">Net · monthly</p>
                    {/* `totals.netCents` and nothing else. The overall discount is
                        already apportioned into the line nets, so subtracting
                        `subscriptionDiscount` here would double-discount. */}
                    <p className="text-lg font-bold tabular-nums text-emerald-700">
                      {preview ? money(payableMonthlyCents(preview)) : '—'}
                    </p>
                  </div>
                </div>
              )}

              {stale && !inputErrors.length && !previewError && !unverified && (
                <p className="text-xs text-stone-500 mt-2">
                  Recalculating — these figures are for the previous plan until billing-service answers.
                </p>
              )}
            </div>

            {/* Errors ------------------------------------------------------ */}
            {composeErrors.length > 0 && (
              <Note tone="danger">
                <strong>{composeErrors.length} package(s) cannot be priced, so this plan cannot be activated.</strong>
                <ul className="mt-1.5 space-y-0.5">
                  {composeErrors.map((e, i) => (
                    <li key={`${e.packageId}-${i}`}>
                      <strong>{catalog.get(e.packageId)?.name || e.packageId}</strong>
                      <span className="font-mono text-[11px] text-red-700"> ({e.packageId})</span>: {e.reason}
                    </li>
                  ))}
                </ul>
              </Note>
            )}

            {refusal && refusal.length > 0 && (
              <Note tone="danger">
                <strong>Activation was refused and nothing changed.</strong>
                <ul className="mt-1.5 space-y-0.5">
                  {refusal.map((e, i) => (
                    <li key={`${e.packageId}-${i}`}>
                      <strong>{catalog.get(e.packageId)?.name || e.packageId}</strong>: {e.reason}
                    </li>
                  ))}
                </ul>
              </Note>
            )}

            {/* Warnings ---------------------------------------------------- */}
            {(preview?.warnings?.length ?? 0) > 0 && (
              <div className="space-y-2">
                {preview!.warnings.map((w, i) => (
                  <Note key={`${w.code}-${w.packageId || w.locationId || i}`} tone="warn">
                    <strong>{WARNING_TITLE[w.code] || 'Worth knowing'}
                      {w.packageId ? ` — ${catalog.get(w.packageId)?.name || w.packageId}` : ''}
                      {w.locationId ? ` — ${locationNames[w.locationId] || w.locationId}` : ''}.
                    </strong>{' '}
                    {w.message}
                    {w.code === 'placed_nowhere' && (
                      <> This package is sold but bills at no location. Unless that is deliberate — a plan covered
                        by the free-location quota is exactly this — the tenant is being UNDER-BILLED.</>
                    )}
                    {/* §14/C2, the leak this editor exists to make visible: a
                        store opened after the plan was recorded is in no
                        placement list, so it bills nothing and nothing says so. */}
                    {w.code === 'location_bills_nothing' && (
                      <> {focusLocationId === w.locationId
                        ? 'That is this store. Switch on the packages it should be billed for, above.'
                        : 'Open “Change price” on that store to place the packages it should be billed for — unless it is deliberately free.'}</>
                    )}
                  </Note>
                ))}
              </div>
            )}

            {/* Per-location breakdown -------------------------------------- */}
            <div className="rounded-2xl border border-stone-200 bg-white text-stone-800 overflow-hidden">
              <div className="px-3.5 py-2.5 border-b border-stone-200 bg-stone-50 text-stone-700 text-[11px] uppercase tracking-wide flex items-center gap-1.5">
                <MapPin size={12} /> Per location — what each store is charged
              </div>
              {!preview ? (
                <p className="px-3.5 py-4 text-sm text-stone-500">Not priced yet.</p>
              ) : preview.perLocation.length === 0 ? (
                <p className="px-3.5 py-4 text-sm text-stone-500">
                  This tenant has no active locations, so this plan bills nothing until one exists.
                </p>
              ) : (
                <div className={`divide-y divide-stone-100 ${stale ? 'opacity-40' : ''}`}>
                  {preview.perLocation.map((loc) => {
                    // A sum of the server's own line nets — the exact amount this
                    // location is charged, not a recomputation of it.
                    const subtotal = loc.items.reduce((sum, item) => sum + item.netCents, 0);
                    const isFocus = loc.locationId === focusLocationId;
                    return (
                      <div
                        key={loc.locationId}
                        className={`px-3.5 py-2.5 ${isFocus ? 'bg-emerald-50/60' : ''}`}
                      >
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <span className="text-sm font-semibold text-stone-800">
                            {locationNames[loc.locationId] || loc.locationId}
                            {isFocus && <span className="ml-2 text-[11px] font-normal text-emerald-700">this store</span>}
                          </span>
                          <span className="text-sm font-bold tabular-nums text-stone-900">
                            {money(subtotal)}<span className="text-stone-400 font-normal">/mo</span>
                          </span>
                        </div>
                        {loc.items.length === 0 ? (
                          // The leak's signature (§14/C2), in the place Task 7
                          // already rendered it: a store that exists and is
                          // billed nothing while the rest of the plan bills.
                          <p className={`text-xs mt-0.5 ${
                            preview.totals.netCents > 0 ? 'text-amber-700 font-medium' : 'text-stone-500'
                          }`}>
                            Bills nothing on this plan.
                            {preview.totals.netCents > 0 && (
                              <> This store is open and charged nothing — check that is deliberate.</>
                            )}
                          </p>
                        ) : (
                          <ul className="mt-1 space-y-0.5">
                            {loc.items.map((item) => (
                              <li key={item.packageId} className="text-xs text-stone-600 flex justify-between gap-3">
                                <span className="truncate">
                                  {catalog.get(item.packageId)?.name || item.packageId}
                                  {item.discount && (
                                    <span className="text-amber-700"> · {describeDiscount(item.discount)}</span>
                                  )}
                                  {!!item.overallDiscountCents && (
                                    <span className="text-amber-700"> · −{money(item.overallDiscountCents)} overall</span>
                                  )}
                                </span>
                                <span className="tabular-nums shrink-0">
                                  <span className="text-stone-400 line-through mr-1.5">
                                    {item.listCents !== item.netCents ? money(item.listCents) : ''}
                                  </span>
                                  {money(item.netCents)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Today vs after (D12) ----------------------------------------
                Composed against composed. The composed net becomes ONE managed
                software line per location, ADDED BESIDE the base plan line the
                sync never touches — so the base subscriptions are stated below
                rather than folded in, which would make this read as the whole
                bill changing. */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-2xl border border-stone-200 bg-stone-50 text-stone-800 p-4">
                <p className="text-[10px] uppercase tracking-wider text-stone-500">Packages billing today</p>
                <p className="text-2xl font-bold tabular-nums text-stone-900">
                  {composedTodayKnown ? money(composedTodayCents) : '—'}
                  {composedTodayKnown && <span className="text-sm font-medium text-stone-500">/mo</span>}
                </p>
                <p className="text-[11px] text-stone-500 mt-0.5">
                  {!composedTodayKnown
                    ? 'the plan in force could not be priced — this is unknown, not zero'
                    : current
                      ? "the active plan assignment, composed by billing-service at today's catalog prices"
                      : 'no plan assignment, so nothing is billed by composition for this tenant yet'}
                </p>
              </div>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-900 p-4">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700">After activating</p>
                <p className="text-2xl font-bold tabular-nums text-stone-900">
                  {composedCents === null ? '—' : money(composedCents)}
                  <span className="text-sm font-medium text-stone-500">/mo</span>
                </p>
                <p className="text-[11px] text-emerald-800 mt-0.5">
                  {composedCents === null
                    ? 'not priced yet'
                    : delta === null
                      ? 'no comparison — today’s composed amount is unknown'
                      : delta === 0
                        ? 'no change to the composed amount'
                        : `${delta > 0 ? '+' : '−'}${money(Math.abs(delta))} versus today`}
                </p>
              </div>
            </div>

            {/* Errors on the plan ALREADY SOLD — beside the "today" tile, in
                the same visual language as the new-plan errors below. The
                "Packages billing today" figure above is still correct — an
                unpriceable package bills nothing — but this is the one screen
                that exists to say what the tenant was sold, and it must not
                stay silent about a broken line on their active plan. */}
            <ActivePlanErrorsNote
              errors={currentErrors.map((e) => ({ ...e, name: catalog.get(e.packageId)?.name || e.packageId }))}
            />

            {/* This store, on its own. The tile pair above is the tenant's
                whole composed bill; an operator who opened "Change price" on
                one store is answering a question about that store. */}
            {focusLocationId && (
              <div className="rounded-2xl border border-stone-200 bg-white text-stone-800 p-4">
                <p className="text-[10px] uppercase tracking-wider text-stone-500">
                  {focusLocationName || focusLocationId} — packages
                </p>
                <p className="text-2xl font-bold tabular-nums text-stone-900">
                  {preview && !stale ? money(focusNetCents) : '—'}
                  <span className="text-sm font-medium text-stone-500">/mo</span>
                </p>
                <p className="text-[11px] text-stone-500 mt-0.5">
                  {!preview || stale
                    ? 'not priced yet'
                    : focusChange
                      ? focusChange.deltaCents === 0
                        ? `unchanged from ${money(focusChange.beforeCents)}/mo`
                        : `was ${money(focusChange.beforeCents)}/mo · ${focusChange.deltaCents > 0 ? '+' : '−'}${money(Math.abs(focusChange.deltaCents))}`
                      : 'the packages this store is charged for'}
                </p>
              </div>
            )}

            <p className="text-[11px] text-stone-500">
              Base plan and per-location subscriptions ({money(baseSubscriptionCents)}/mo) bill separately and are
              not changed by activating a plan — the composed amount is added beside them.
            </p>

            {/* Actions ----------------------------------------------------- */}
            <div className="flex items-center justify-end gap-2 flex-wrap">
              {blockedReason && <span className="text-xs text-stone-500 flex-1 min-w-[12rem]">{blockedReason}</span>}
              <button
                onClick={onSaveDraft}
                disabled={saving || inputErrors.length > 0}
                className="px-4 py-3 rounded-xl border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-50 disabled:opacity-40 min-h-[44px] inline-flex items-center gap-1.5"
              >
                {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Plus size={14} />}
                {draftId ? 'Update draft' : 'Save draft'}
              </button>
              <button
                onClick={() => { setAcknowledged(false); setConfirmOpen(true); }}
                disabled={!!blockedReason || activating}
                className="px-5 py-3 rounded-xl bg-stone-900 text-white text-sm font-bold hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px] inline-flex items-center gap-1.5"
              >
                <Check size={15} /> {focusLocationId ? 'Review & publish' : 'Review & activate'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* THE BEFORE -> AFTER GATE (D12, spec §14/C2).
          Nobody's bill changes without a human seeing what it is now, what it
          becomes, and WHICH PACKAGE moved. The comparison below is the server's
          own — `changeSummary` from the preview — and the confirm button stays
          disabled until the operator explicitly acknowledges it. A gate that
          can be clicked past without reading is not a gate. */}
      <ConfirmDialog
        open={confirmOpen}
        title={focusLocationId ? `Publish this price for ${focusLocationName || 'this store'}?` : 'Activate this plan?'}
        confirmLabel={focusLocationId ? 'Publish' : 'Activate'}
        busy={activating}
        blocked={blockedReason || (!acknowledged
          ? 'Read the change below, then tick the box to confirm you have seen both numbers.'
          : null)}
        onConfirm={onActivate}
        onClose={() => setConfirmOpen(false)}
      >
        {/* WHOSE MONEY THIS IS.
            In the location editor the operator opened one store's price, so the
            headline is THAT STORE'S before → after. The tenant total is shown
            beneath it, said out loud as the whole agreement across N stores —
            because this editor can also change the plan for every store, and
            hiding that would trade one mislabelling for a worse omission.
            Outside focus mode there is only one pair and it is the tenant's. */}
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div className="rounded-xl border border-stone-200 bg-stone-50 text-stone-800 p-3">
            <p className="text-[10px] uppercase tracking-wider text-stone-500">
              {focusLocationId ? `${focusLocationName || 'This store'} today` : 'Packages today'}
            </p>
            <p className="text-xl font-bold tabular-nums text-stone-900">
              {!composedTodayKnown
                ? '—'
                : focusLocationId
                  ? (focusBeforeCents === null ? '—' : money(focusBeforeCents))
                  : money(composedTodayCents)}
            </p>
            <p className="text-[11px] text-stone-500">
              {!composedTodayKnown
                ? 'unknown — could not be priced'
                : focusLocationId
                  ? 'this store’s packages, on the plan in force'
                  : current ? 'the active assignment' : 'no plan assignment yet'}
            </p>
          </div>
          <ArrowRight size={18} className="text-stone-400" />
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 p-3">
            <p className="text-[10px] uppercase tracking-wider text-emerald-700">
              {focusLocationId ? `${focusLocationName || 'This store'} from now on` : 'From now on'}
            </p>
            <p className="text-xl font-bold tabular-nums text-stone-900">
              {focusLocationId
                ? (focusAfterCents === null ? '—' : money(focusAfterCents))
                : composedCents === null ? '—' : money(composedCents)}
            </p>
            <p className="text-[11px] text-emerald-800">
              {focusLocationId
                ? 'this store’s packages'
                : preview ? `${preview.perLocation.length} location(s)` : ''}
            </p>
          </div>
        </div>

        {focusLocationId && (
          <div className="rounded-xl border border-stone-200 bg-white text-stone-800 px-3 py-2.5 text-xs">
            <strong>The whole agreement&rsquo;s packages</strong>, across{' '}
            {preview ? preview.perLocation.length : 0} store
            {(preview?.perLocation.length ?? 0) === 1 ? '' : 's'}:{' '}
            <span className="tabular-nums">
              {composedTodayKnown ? money(composedTodayCents) : '—'}
            </span>{' '}
            → <span className="tabular-nums font-semibold">
              {composedCents === null ? '—' : money(composedCents)}
            </span>{' '}
            a month. The figures above are {focusLocationName || 'this store'} alone.
          </div>
        )}
        {/* PACKAGE BY PACKAGE. The two figures above say how much; this says
            WHAT — which is the half an operator can actually check against what
            they agreed with the customer. It is the same summary the tenant's
            email is rendered from, so the screen and the email cannot disagree. */}
        {summary ? (
          summary.changes.length === 0 ? (
            <Note tone="warn">
              This plan bills no packages at all. Publishing it supersedes whatever came before and charges
              nothing for software.
            </Note>
          ) : (
            <div className="rounded-xl border border-stone-200 overflow-hidden text-stone-800">
              <div className="px-3 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wide text-stone-600">
                {focusLocationId
                  ? `What changes at ${focusLocationName || 'this store'}, package by package`
                  : 'What changes, package by package'}
              </div>
              <table className="w-full text-xs">
                <tbody className="divide-y divide-stone-100">
                  {/* In focus mode every figure in this table is THIS STORE'S —
                      `locationChangeRows` re-cuts the same server-composed lines
                      for one location. The tenant-wide movement is not dropped,
                      it is stated underneath the package name and labelled
                      "all stores", so the two can never be mistaken for each
                      other. */}
                  {(focusRows ?? summary.changes.map((c) => ({
                    packageId: c.packageId, name: c.name, kind: c.kind,
                    beforeCents: c.beforeCents, afterCents: c.afterCents,
                    deltaCents: c.deltaCents, billsElsewhere: false, tenant: c,
                  } as LocationPackageChange))).map((row) => {
                    const c = row.tenant;
                    const quiet = row.kind === 'unchanged' && !c.movedLocations;
                    const tenantMoved = c.kind !== 'unchanged' || c.movedLocations;
                    return (
                      <tr key={row.packageId} className={quiet ? 'text-stone-500' : ''}>
                        <td className="px-3 py-2">
                          <div className="font-semibold">
                            {row.name || catalog.get(row.packageId)?.name || row.packageId}
                          </div>
                          {c.movedLocations && (
                            <div className="text-[11px] text-amber-700">
                              moves to {(c.afterLocationIds.length
                                ? c.afterLocationIds.map((id) => locationNames[id] || id).join(', ')
                                : 'no location')}
                            </div>
                          )}
                          {/* The whole-agreement figures, only where they differ
                              from this store's — otherwise it is noise. */}
                          {focusRows && tenantMoved && row.billsElsewhere && (
                            <div className="text-[11px] text-stone-500">
                              all stores: {c.kind === 'added' ? '—' : `${money(c.beforeCents)}/mo`}
                              {' → '}
                              {c.kind === 'removed' ? 'no longer billed' : `${money(c.afterCents)}/mo`}
                            </div>
                          )}
                          {focusRows && row.kind === 'unchanged' && !row.billsElsewhere
                            && row.afterCents === 0 && (
                            <div className="text-[11px] text-stone-500">not billed at this store</div>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <Chip tone={CHANGE_TONE[row.kind]}>{CHANGE_LABEL[row.kind] || row.kind}</Chip>
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-stone-500 whitespace-nowrap">
                          {row.kind === 'added' ? '—' : `${money(row.beforeCents)}/mo`}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                          {row.kind === 'removed'
                            ? <span className="text-red-700">no longer billed</span>
                            : `${money(row.afterCents)}/mo`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {focusRows && (
                <p className="px-3 py-2 bg-stone-50 border-t border-stone-200 text-[11px] text-stone-600">
                  Every figure in this table is {focusLocationName || 'this store'}&rsquo;s share. Where a package
                  also bills elsewhere, the whole-agreement movement is shown under its name.
                </p>
              )}
            </div>
          )
        ) : (
          <Note tone="warn">
            The package-by-package comparison is not ready yet. Nothing may be published until billing-service
            has priced both the plan in force and this one.
          </Note>
        )}

        <p>
          This supersedes the current assignment and becomes the record of what this tenant was sold — including
          every discount and why it was given. <strong>It takes effect immediately</strong>, and the subscription
          is prorated for the rest of the current period.
        </p>
        <p>
          {noOp?.kind === 'same-money'
            ? <>Our admins are emailed this. <strong>The tenant is not</strong> — nothing they are billed for
              changed, so there is nothing to tell them.</>
            : <>The tenant and our admins are emailed exactly this breakdown. If the subscription cannot be
              updated, the customer is <strong>not</strong> emailed and you will be told why.</>}
        </p>
        <p>
          Base plan and per-location subscriptions ({money(baseSubscriptionCents)}/mo) are billed separately and
          are not changed by this.
        </p>
        {(preview?.warnings?.length ?? 0) > 0 && (
          <Note tone="warn">
            {preview!.warnings.length} thing(s) worth checking first: {preview!.warnings.map((w) => w.message).join('; ')}.
          </Note>
        )}

        {/* The acknowledgement. A checkbox rather than a second button so the
            reason it is disabled stays visible next to the thing being agreed. */}
        <label className="flex items-start gap-2.5 rounded-xl border border-stone-300 bg-stone-50 px-3 py-2.5 cursor-pointer text-stone-800">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 w-4 h-4 shrink-0 accent-emerald-600"
          />
          {/* THE MOMENT OF CONSENT. The figures in this sentence must be the
              ones being consented to: in the location editor that is THIS
              STORE'S, with the tenant total named separately as the whole
              agreement. It previously read "this store" against two
              tenant-wide totals. */}
          <span className="text-xs">
            I have read the before → after above.{' '}
            {!composedTodayKnown
              ? <>The amount in force could not be priced, so only the new amount is known.</>
              : focusLocationId
                ? <>This changes what <strong>{focusLocationName || 'this store'}</strong> is charged from{' '}
                  <strong>{focusBeforeCents === null ? '—' : money(focusBeforeCents)}</strong> to{' '}
                  <strong>{focusAfterCents === null ? '—' : money(focusAfterCents)}</strong> a month, and the whole
                  agreement across every store from <strong>{money(composedTodayCents)}</strong> to{' '}
                  <strong>{composedCents === null ? '—' : money(composedCents)}</strong>.</>
                : <>This changes what this tenant is charged from{' '}
                  <strong>{money(composedTodayCents)}</strong> to{' '}
                  <strong>{composedCents === null ? '—' : money(composedCents)}</strong> a month.</>}
          </span>
        </label>
      </ConfirmDialog>
    </Card>
  );
};

export default PlanAssignmentBuilder;
